<script lang="ts">
  import Icon from './Icon.svelte';
  import FieldTypeIcon from './FieldTypeIcon.svelte';
  import type { ApiField } from '../lib/data-tables-api';

  type Granularity = 'day' | 'week' | 'month' | 'quarter' | 'year';

  let {
    mode,
    kanbanFieldKey,
    groupableFields,
    granularity = 'month',
    onModeChange,
    onKanbanFieldChange,
    onGranularityChange,
  }: {
    mode: 'list' | 'kanban';
    kanbanFieldKey: string | null;
    /** Поля по яких можна групувати (усі типи крім обчислюваних/реляційних). */
    groupableFields: ApiField[];
    granularity?: Granularity;
    onModeChange: (m: 'list' | 'kanban') => void;
    onKanbanFieldChange: (key: string) => void;
    onGranularityChange?: (g: Granularity) => void;
  } = $props();

  let open = $state(false);

  const GRANS: { key: Granularity; label: string }[] = [
    { key: 'day', label: 'День' },
    { key: 'week', label: 'Тиждень' },
    { key: 'month', label: 'Місяць' },
    { key: 'quarter', label: 'Квартал' },
    { key: 'year', label: 'Рік' },
  ];

  const currentField = $derived(groupableFields.find((f) => f.fieldKey === kanbanFieldKey) ?? null);
  const isDateField = $derived(currentField?.type === 'date' || currentField?.type === 'datetime');


  const currentLabel = $derived(
    mode === 'list'
      ? 'Вид списком'
      : `Картковий вид по: ${groupableFields.find((f) => f.fieldKey === kanbanFieldKey)?.label ?? '…'}`,
  );

  function selectList() {
    onModeChange('list');
    open = false;
  }

  function selectKanban(fieldKey: string) {
    onKanbanFieldChange(fieldKey);
    onModeChange('kanban');
    open = false;
  }

  function onKeydown(e: KeyboardEvent) {
    if (e.key === 'Escape') open = false;
  }
</script>

<svelte:window onkeydown={onKeydown} />

<div class="relative">
  <button
    type="button"
    onclick={() => (open = !open)}
    class="inline-flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-[11px] font-semibold transition-colors
      {mode === 'kanban'
        ? 'border-[var(--color-pu)] bg-purple-50 text-[var(--color-pu-3)]'
        : 'border-[var(--color-border-3)] bg-white text-[var(--color-text)] hover:bg-[var(--color-bg-3)]'}"
  >
    <span class="flex"><Icon name={mode === 'list' ? 'listView' : 'boardView'} size={15} /></span>
    <span class="hidden sm:inline max-w-[150px] truncate">{currentLabel}</span>
    <Icon name="chevronDown" size={14} class="text-[var(--color-text-muted)]" />
  </button>

  {#if open}
    <div class="fixed inset-0 z-30" role="presentation" onclick={() => (open = false)}></div>

    <div class="absolute left-0 top-full mt-1.5 z-40 w-64 rounded-xl border border-[var(--color-border-3)] bg-white shadow-xl overflow-hidden">
      <!-- List view -->
      <button
        type="button"
        onclick={selectList}
        class="w-full flex items-center gap-3 px-3.5 py-3 text-left hover:bg-[var(--color-bg-3)] transition-colors
          {mode === 'list' ? 'bg-purple-50 text-[var(--color-pu-3)]' : ''}"
      >
        <span class="w-6 flex justify-center shrink-0"><Icon name="listView" size={16} /></span>
        <span class="text-[12.5px] font-semibold">Вид списком</span>
      </button>

      {#if groupableFields.length > 0}
        <div class="px-3.5 pt-2 pb-1 text-[9.5px] font-bold uppercase tracking-wider text-[var(--color-text-muted-2)] border-t border-[var(--color-border-3)]">
          Картковий вид по:
        </div>
        <div class="max-h-[260px] overflow-y-auto">
          {#each groupableFields as f (f.fieldKey)}
            <button
              type="button"
              onclick={() => selectKanban(f.fieldKey)}
              class="w-full flex items-center gap-3 px-3.5 py-2.5 text-left hover:bg-[var(--color-bg-3)] transition-colors
                {mode === 'kanban' && kanbanFieldKey === f.fieldKey ? 'bg-purple-50 text-[var(--color-pu-3)]' : ''}"
            >
              <span class="w-6 flex justify-center shrink-0"><FieldTypeIcon type={f.type} size={15} /></span>
              <span class="text-[12px] font-medium">{f.label}</span>
            </button>
          {/each}
        </div>

        {#if mode === 'kanban' && isDateField}
          <div class="px-3.5 pt-2 pb-1 text-[9.5px] font-bold uppercase tracking-wider text-[var(--color-text-muted-2)] border-t border-[var(--color-border-3)]">
            Групувати за періодом:
          </div>
          <div class="flex flex-wrap gap-1 px-3 pb-3 pt-1">
            {#each GRANS as g (g.key)}
              <button
                type="button"
                onclick={() => onGranularityChange?.(g.key)}
                class="rounded-md border px-2.5 py-1 text-[11px] font-medium transition-colors
                  {granularity === g.key
                    ? 'border-[var(--color-pu)] bg-purple-50 text-[var(--color-pu-3)]'
                    : 'border-[var(--color-border-3)] bg-white hover:bg-[var(--color-bg-3)]'}"
              >{g.label}</button>
            {/each}
          </div>
        {/if}
      {:else}
        <div class="px-3.5 py-3 text-[11px] text-[var(--color-text-muted-2)] italic border-t border-[var(--color-border-3)]">
          Немає полів для групування.
        </div>
      {/if}
    </div>
  {/if}
</div>
